/**
 * Challenge Service
 *
 * Service for managing challenges including creation, updating,
 * deletion, status updates, and listing user challenges.
 * Extends the base CrudService with separate detail (show)
 * and list (index) shapes.
 */

import { ZodError } from 'zod';

import {
  InvalidDataError,
  invalidDataErrorFromZod,
} from '../errors/invalid-data.error';
import { apiClient } from './api/client';
import { rethrowTyped } from './api/errors';
import { CrudService } from './crud.service';
import {
  validateChallenge,
  type ChallengeInput,
} from '../validation/challenge.schemas';

import type { ID } from '../types';
import type {
  ChallengeStatusTransition,
  ChallengeSummary,
  ChallengeDetail,
  ChallengeDetailParticipant,
  ChallengeParticipantStatus,
} from '../types/domain/challenge.types';

// =============================================================================
// CHALLENGE SERVICE CLASS
// =============================================================================

export class ChallengeService extends CrudService<
  ChallengeDetail,
  ChallengeSummary,
  ChallengeInput
> {
  constructor(cacheTTL?: number) {
    super({
      baseUrl: '/challenge',
      enableCaching: true,
      cacheTTL: cacheTTL || 5 * 60 * 1000, // 5 minutes default
    });
  }

  // ---------------------------------------------------------------------------
  // CORE CHALLENGE OPERATIONS
  // ---------------------------------------------------------------------------

  /**
   * Every challenge the user takes part in.
   *
   * GET /api/user/{user}/challenge
   *
   * The whole list (`getAll`), because the Challenges page counts, filters and
   * picks its hero across all of them. The endpoint accepts no filters, so
   * none are sent.
   */
  public async getUserChallenges(
    username: string
  ): Promise<ChallengeSummary[]> {
    if (!username) {
      throw new Error('Username is required to fetch user challenges');
    }

    try {
      return await this.withContext({
        template: '/user/{username}/challenge',
        parameters: { username },
        cacheNamespace: `${username}-challenges`,
      }).getAll();
    } catch (error) {
      console.error(`Failed to fetch challenges for user ${username}:`, error);
      rethrowTyped(error, 'Unable to fetch user challenges. Please try again.');
    }
  }

  /**
   * Get detailed challenge information by ID.
   *
   * GET /api/challenge/{challenge}
   */
  public async getChallengeById(id: ID): Promise<ChallengeDetail> {
    if (!id) {
      throw new Error('Challenge ID is required');
    }

    try {
      const challenge = await this.getById(id);
      return this.transformResponse(challenge);
    } catch (error) {
      console.error(`Failed to fetch challenge ${id}:`, error);
      rethrowTyped(
        error,
        'Unable to fetch challenge details. Please check the challenge ID and try again.'
      );
    }
  }

  /**
   * Create a new challenge for a user.
   *
   * POST /api/user/{user}/challenge
   * Uses challenge.schemas.ts for client-side validation.
   */
  public async createUserChallenge(
    username: string,
    data: ChallengeInput
  ): Promise<ChallengeSummary> {
    if (!username) {
      throw new Error('Username is required to create a challenge');
    }

    try {
      this.validateChallengeData(data);

      const transformedData = this.transformRequest(data);

      const created = await this.withContext({
        template: '/user/{username}/challenge',
        parameters: { username },
        cacheNamespace: `${username}-challenges`,
      }).create(transformedData);

      return created;
    } catch (error) {
      console.error('Failed to create challenge:', error);
      // Preserves a typed ApiErrorClass (incl. any 402) and InvalidDataError;
      // only a genuinely untyped throw gets the fallback message.
      rethrowTyped(
        error,
        'Unable to create challenge. Please check your data and try again.'
      );
    }
  }

  /**
   * Update an existing challenge by ID.
   *
   * PUT/PATCH /api/challenge/{challenge}
   * Uses challenge.schemas.ts for client-side validation.
   */
  public async updateChallenge(
    id: ID,
    data: ChallengeInput
  ): Promise<ChallengeSummary> {
    if (!id) {
      throw new Error('Challenge ID is required to update a challenge');
    }

    try {
      this.validateChallengeData(data);

      const transformed = this.transformRequest(data);
      const updated = await this.update(id, transformed, { partial: true });

      return updated;
    } catch (error) {
      console.error(`Failed to update challenge ${id}:`, error);
      rethrowTyped(
        error,
        'Unable to update challenge. Please check your data and try again.'
      );
    }
  }

  /**
   * Delete a challenge by ID.
   *
   * DELETE /api/challenge/{challenge}
   */
  public async deleteChallenge(id: ID): Promise<void> {
    if (!id) {
      throw new Error('Challenge ID is required to delete a challenge');
    }

    try {
      await this.delete(id);
    } catch (error) {
      console.error(`Failed to delete challenge ${id}:`, error);
      rethrowTyped(error, 'Unable to delete challenge. Please try again.');
    }
  }

  /**
   * Restore a soft-deleted challenge.
   *
   * PATCH /api/undo-delete/challenge/{challenge}
   */
  public async restoreChallenge(id: ID): Promise<void> {
    if (!id) {
      throw new Error('Challenge ID is required to restore a challenge');
    }

    try {
      await this.undoDelete('challenge', id);
    } catch (error) {
      console.error(`Failed to restore challenge ${id}:`, error);
      rethrowTyped(error, 'Unable to restore challenge. Please try again.');
    }
  }

  // ---------------------------------------------------------------------------
  // CHALLENGE STATUS OPERATIONS
  // ---------------------------------------------------------------------------
  // NOTE:
  // The status endpoint returns 200 with no data, which does not fit
  // CrudService.update’s expectation of a non-empty response. To avoid
  // false errors, these operations call apiClient directly and then
  // invalidate the relevant caches.

  private getChallengeStatusUrl(id: ID): string {
    return `/challenge/${id}/status`;
  }

  /**
   * Update challenge status.
   *
   * PATCH /api/challenge/{challenge}/status
   * Backend returns 200 OK with no data.
   *
   * Takes {@link ChallengeStatusTransition} rather than the domain
   * `ChallengeStatus`: the endpoint accepts only `active` and `cancelled`, and
   * validates against the backend enum's VALUES — sending the display label
   * "Active" 422s.
   */
  public async updateChallengeStatus(
    id: ID,
    status: ChallengeStatusTransition
  ): Promise<void> {
    if (!id) {
      throw new Error('Challenge ID is required to update status');
    }

    try {
      await apiClient.patch(this.getChallengeStatusUrl(id), { status });

      // Contextless, so this widens across namespaces — the challenge list
      // is cached per user (`{username}-challenges`) and a scoped
      // invalidation would match `getAll…` and miss it entirely.
      this.invalidateChallengeCache(id);
    } catch (error) {
      console.error(`Failed to update status for challenge ${id}:`, error);
      rethrowTyped(
        error,
        'Unable to update challenge status. Please try again.'
      );
    }
  }

  // ---------------------------------------------------------------------------
  // CACHE MANAGEMENT
  // ---------------------------------------------------------------------------

  /**
   * Invalidate the cached challenge detail and lists.
   *
   * Public for writers outside this service whose change shows in a
   * challenge's detail or the Challenges list — the participant service's
   * invites, rescinds, resends and withdrawals — which the base class's
   * write-path invalidation cannot see. Without an `id`, every cached detail
   * goes. Contextless, so it widens across the per-user list namespaces.
   */
  public invalidateChallengeCache(id?: ID): void {
    this.invalidateCache(
      ['getAll', 'getPaginated', 'getById', 'search', 'count'],
      id
    );
  }

  // ---------------------------------------------------------------------------
  // VALIDATION AND TRANSFORMATION
  // ---------------------------------------------------------------------------

  /**
   * Validate challenge payload using Zod schema from challenge.schemas.ts.
   */
  protected validateChallengeData(data: ChallengeInput): void {
    try {
      validateChallenge(data);
    } catch (error) {
      if (error instanceof ZodError) {
        throw invalidDataErrorFromZod("Couldn't save the challenge", error, {
          originalData: data,
          details: { service: 'ChallengeService' },
        });
      }

      throw new InvalidDataError(
        "Couldn't save the challenge: the data is in an unexpected format",
        {
          originalData: data,
          details: { service: 'ChallengeService' },
        }
      );
    }
  }

  /**
   * Transform request data before sending to API
   * Hook for converting data formats if needed.
   */
  protected override transformRequest(
    data: Partial<ChallengeInput>
  ): Partial<ChallengeInput> {
    return {
      ...data,
    };
  }

  /**
   * Normalize response from API for frontend consumption.
   *
   * Two known API warts are absorbed here rather than being pushed onto every
   * consumer:
   *
   * 1. `status` may arrive as a `ParticipantStatus` LABEL ("Accepted") instead
   *    of its value ("accepted") — `ChallengeParticipantCollection` returns
   *    labels, so both shapes are in circulation. Left unnormalized, grouping
   *    silently drops every racer into "pending".
   * 2. `progress` may arrive as a numeric string ("94.72"), the same wart
   *    `toProgressNumber` exists for on the list page.
   * 3. `milestones` arrive in no guaranteed order — a real payload returned
   *    `[50, 25]`. A ladder read top-down has to climb, and the row pips are
   *    positional, so both are sorted here once rather than at each render.
   */
  protected override transformResponse(data: ChallengeDetail): ChallengeDetail {
    return {
      ...data,
      participants: (data.participants ?? []).map(normalizeParticipant),
      milestones: [...(data.milestones ?? [])].sort(
        (a, b) => a.threshold - b.threshold
      ),
    };
  }
}

/** Coerce a possibly-string numeric to a finite number, defaulting to 0. */
function toNumber(value: number | string | null | undefined): number {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const parsed = parseFloat(String(value ?? ''));
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Accept either the enum value or its display label for participant status.
 *
 * Unknown values fall back to `invited`: the most conservative reading, since
 * an unrecognised participant should never be counted as racing.
 */
function toParticipantStatus(value: unknown): ChallengeParticipantStatus {
  const normalized = String(value ?? '').toLowerCase();
  return normalized === 'accepted' ||
    normalized === 'declined' ||
    normalized === 'withdrawn'
    ? normalized
    : 'invited';
}

function normalizeParticipant(
  participant: ChallengeDetailParticipant
): ChallengeDetailParticipant {
  return {
    ...participant,
    status: toParticipantStatus(participant.status),
    progress: toNumber(participant.progress),
    points: toNumber(participant.points),
    // Sorted for the same reason as the ladder: these read out as a list
    // ("banked 25%, 50%"), and payloads have arrived descending.
    milestones_achieved: [...(participant.milestones_achieved ?? [])].sort(
      (a, b) => a - b
    ),
  };
}

// =============================================================================
// SERVICE INSTANCE
// =============================================================================

/**
 * Default challenge service instance.
 */
export const challengeService = new ChallengeService();
